import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const navLinks = [
  { href: "/why", label: "Why" },
  { href: "/how", label: "How" },
  { href: "/what", label: "What", hoverLabel: "Services" },
  { href: "/who", label: "Who" },
  { href: "/projects", label: "Projects", hoverLabel: "Works" },
];

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredLink, setHoveredLink] = useState<string | null>(null);
  const location = useLocation();

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  return (
    <motion.header
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1, ease: [0.23, 1, 0.32, 1], delay: 0.5 }}
      // Glassmorphism background: semi-transparent purple with a backdrop blur and subtle border
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 bg-purple/80 backdrop-blur-lg border-b border-white/10 shadow-sm py-3 md:py-4`}
    >
      <nav className="container-wide">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 flex-shrink-0 group">
            <img src="/assets/images/nueva_logo.png" alt="Nueva Media Solutions Logo" className="h-8 object-contain brightness-0 invert" />
            <div className="flex flex-col md:flex-row md:items-center">
              <span className={`hidden md:block pl-3 text-[10px] font-bold tracking-[0.2em] group-hover:text-[#c0ff09] transition-colors duration-300 text-white/60`}>
                Kerala | Pune | Qatar
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link, i) => (
              <motion.div
                key={link.href}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.8 + (i * 0.1), ease: "easeOut" }}
              >
                <Link
                  to={link.href}
                  onMouseEnter={() => setHoveredLink(link.href)}
                  onMouseLeave={() => setHoveredLink(null)}
                  style={{ fontFamily: '"Helvetica Now Light", "Helvetica Neue", Helvetica, Arial, sans-serif' }}
                  className={`text-sm tracking-wide transition-colors duration-300 ${location.pathname === link.href
                    ? "text-[#c0ff09]" // Active link color
                    : "text-white/90 hover:text-[#c0ff09]" // Normal and Hover color
                    }`}
                >
                  {hoveredLink === link.href && link.hoverLabel ? link.hoverLabel : link.label}
                </Link>
              </motion.div>
            ))}
          </div>

          {/* CTA */}
          <motion.div
            className="hidden lg:block"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 1.5, ease: "easeOut" }}
          >
            <Button variant="signal" size="sm" asChild>
              <Link to="/contact">Start a Project with Us</Link>
            </Button>
          </motion.div>

          {/* Mobile Menu Toggle */}
          <button
            className="lg:hidden p-2 text-white"
            onClick={() => setIsOpen(!isOpen)}
            aria-label="Toggle menu"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </nav>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden absolute top-full left-0 right-0 bg-purple/95 backdrop-blur-xl border-b border-white/10 overflow-hidden shadow-2xl"
          >
            <div className="container-wide py-8 flex flex-col gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onMouseEnter={() => setHoveredLink(link.href)}
                  onMouseLeave={() => setHoveredLink(null)}
                  style={{ fontFamily: '"Helvetica Now Light", "Helvetica Neue", Helvetica, Arial, sans-serif' }}
                  className={`text-lg tracking-wide py-3 transition-colors duration-300 ${location.pathname === link.href
                    ? "text-[#c0ff09]"
                    : "text-white/80 hover:text-white"
                    }`}
                >
                  {hoveredLink === link.href && (link as any).hoverLabel ? (link as any).hoverLabel : link.label}
                </Link>
              ))}
              <div className="pt-6 mt-4 border-t border-white/20">
                <Button variant="signal" size="lg" className="w-full bg-white text-purple hover:bg-white/90" asChild>
                  <Link to="/contact">Start a Project with Us</Link>
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Navigation;
